/* eslint-disable no-undef */
import { getCurrencyOptions, getCurrencySymbol } from './Amalgam';
import { getMOQForProductId } from './Inventory';
import { getAllOptions, getProduct, getProductPictureUrlsAsArray, OPTION_TYPE } from './Product';
import { getProductMarginFromCache } from './ResellerMargin';

export const doesNativeConnectorExist = () => typeof Android !== 'undefined';

export const getNativeConnector = () => Android;

export const nativeMethodExists = (methodName) => {
  if (!doesNativeConnectorExist()) {
    return false;
  }

  return typeof getNativeConnector()[methodName] === 'function';
};

export const nativeChatOnWhatsApp = (message, phone) => {
  getNativeConnector().chatOnWhatsapp(message, phone);
};

export const nativeSendAnalyticsEvent = ({ name, props }) => {
  if (!nativeMethodExists('sendEvent')) {
    return;
  }

  getNativeConnector().sendEvent(name, JSON.stringify(props || {}));
};

const getProductShareData = ({ productId }) => {
  const pictures = getProductPictureUrlsAsArray({ productId });
  const product = getProduct(productId) || {};
  const margin = getProductMarginFromCache({ productId }) || 0;

  let variantColor = '';
  let variantSize = '';
  if (product.isVariant()) {
    ({ COLORS: { optionValue: variantColor } = {}, SIZES: { optionValue: variantSize } = {} } =
      product.optionsMeta || {});
  }

  let showSymbolOnRight = false;
  const currencyOptions = getCurrencyOptions();
  if (currencyOptions.symbolOnRight) {
    showSymbolOnRight = true;
  }

  let colorOptionsData = getAllOptions({ productId, optionType: OPTION_TYPE.COLORS.id });
  if (Array.isArray(colorOptionsData)) {
    colorOptionsData = colorOptionsData.map((optionData) => optionData.optionValue);
  }

  let sizeOptionsData = getAllOptions({ productId, optionType: OPTION_TYPE.SIZES.id });
  if (Array.isArray(sizeOptionsData)) {
    sizeOptionsData = sizeOptionsData.map((optionData) => optionData.optionValue);
  }

  let finalPrice, finalDiscountedPrice;
  if (typeof product.price === 'number') {
    finalPrice = product.price + margin;
  }

  if (typeof product.discounted_price === 'number') {
    finalDiscountedPrice = product.discounted_price + margin;
  }

  return {
    pictures,
    name: product.name,
    description: product.description,
    sku: product.sku,
    price: finalPrice,
    discountedPrice: finalDiscountedPrice,
    moq: getMOQForProductId(productId),
    currencySymbol: getCurrencySymbol(),
    showSymbolOnRight,
    variantColor,
    variantSize,
    colorOptionsData,
    sizeOptionsData
  };
};

export const nativeShareToWhatsApp = ({ productId }) => {
  getNativeConnector().sharePicsToWhatsapp(JSON.stringify(getProductShareData({ productId })));
};

export const nativeShareProductToWhatsAppContact = ({ productId, number, chatMessage }) => {
  if (!number) {
    return;
  }

  if (nativeMethodExists('shareProductToWhatsAppContact')) {
    getNativeConnector().shareProductToWhatsAppContact(
      JSON.stringify(getProductShareData({ productId })),
      number
    );
    return;
  }

  nativeChatOnWhatsApp(chatMessage || '', number);
};

export const nativeShareToAny = ({ productId }) => {
  getNativeConnector().shareToAnyApp(JSON.stringify(getProductShareData({ productId })));
};

export const nativeDownloadProductPictures = ({ productIds }) => {
  if (!Array.isArray(productIds)) {
    return;
  }

  const pictureData = productIds.map((productId) => {
    const product = getProduct(productId) || {};
    const pictureUrls = getProductPictureUrlsAsArray({ productId });
    return { pictureUrls, productId, productTitle: product.name };
  });

  getNativeConnector().downloadFiles(JSON.stringify(pictureData));
};

const getPictureUrlsForAllProducts = ({ productIds }) => {
  const shareData = {
    pictures: []
  };

  if (!Array.isArray(productIds)) {
    return shareData;
  }

  const pictureUrlsSet = productIds.reduce((cummulativeSet, productId) => {
    const pictureUrls = getProductPictureUrlsAsArray({ productId });
    pictureUrls.forEach((url) => cummulativeSet.add(url));
    return cummulativeSet;
  }, new Set());
  shareData.pictures = [...pictureUrlsSet];

  return shareData;
};

const getProductsShareData = (productIds) => {
  if (!Array.isArray(productIds)) {
    return [];
  }

  return productIds.map((productId) => getProductShareData({ productId }));
};

export const nativeMultiShareProductsToWhatsApp = ({ productIds }) => {
  if (nativeMethodExists('shareProductsToWhatsApp')) {
    getNativeConnector().shareProductsToWhatsApp(JSON.stringify(getProductsShareData(productIds)));
    return;
  }

  const pictureShareData = getPictureUrlsForAllProducts({ productIds });
  getNativeConnector().sharePicsToWhatsapp(JSON.stringify(pictureShareData));
};

export const nativeMultiShareProductsToAnyApp = ({ productIds }) => {
  if (nativeMethodExists('shareProductsToAnyApp')) {
    getNativeConnector().shareProductsToAnyApp(JSON.stringify(getProductsShareData(productIds)));
    return;
  }

  const pictureShareData = getPictureUrlsForAllProducts({ productIds });
  getNativeConnector().shareToAnyApp(JSON.stringify(pictureShareData));
};

export const nativeWhatsAppLogin = ({ message, toNumber }) => {
  getNativeConnector().loginUsingWhatsApp(message, toNumber);
};

export const nativePostLoginData = ({ companyId, phone }) => {
  if (nativeMethodExists('postLogin')) {
    getNativeConnector().postLogin(companyId, phone);
    return;
  }

  getNativeConnector().registerTopic(companyId);
};

export const nativeOpenUrl = ({ url }) => {
  if (!url) {
    return;
  }
  getNativeConnector().openLink(url);
};

export const nativeOnLogout = () => {
  if (nativeMethodExists('logout')) {
    getNativeConnector().logout();
  }
};
